/**
 * RegionsPage — Org Structure · Regions.
 *
 * Reads /v3/config/regions + /v3/config/branches (for the branch-assignment
 * grid). Full CRUD via the keyless admin reference surface:
 *   - Save:   POST /v3/admin/regions  (client.retail.admin.reference.regions.upsert)
 *             — optionally reassigns branches into this region in the same
 *             transaction (branchCodesToReassign).
 *   - Delete: SOFT (upsert isActive=false) — matches legacy procedure + the
 *             retired ROP-teams editor. The hard DELETE route refuses when
 *             branches still reference the region, so soft-delete is the safe
 *             default.
 *
 * Branch assignment is ONE-WAY (checking a branch reassigns it into this
 * region; unchecking does NOT move it out — move it from its new region's tab).
 *
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL bearer; no bypass-key secret in bundle).
 * Migrated from ROP-teams Admin (BACKLOG-0579 Batch 2).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ADMIN_AUTH_TOKEN_SENTINEL, ApiError } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, NumberField, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

// Local row — the /v3/config/regions wire returns sortOrder + a nested
// `branches` array (NOT the `branchCodes: string[]` the shared-types Region
// declares). Read by casting, deriving the code roll-up from `branches`.
interface RegionRow {
  regionCode: string;
  regionName: string;
  sortOrder: number | null;
  isActive: boolean;
  branchCodes: ReadonlyArray<string>;
}

interface RegionWire {
  regionCode: string;
  regionName: string;
  sortOrder?: number | null;
  isActive: boolean;
  branches?: ReadonlyArray<{ branchCode: string }>;
  branchCodes?: ReadonlyArray<string>;
}

interface BranchLite {
  branchCode: string;
  branchName: string;
  regionCode: string | null;
}

interface RegionDraft {
  isNew: boolean;
  lockedKey: boolean;
  regionCode: string;
  regionName: string;
  sortOrder: number;
  isActive: boolean;
  assigned: ReadonlySet<string>; // branch codes currently checked
  original: ReadonlySet<string>; // branch codes in this region at edit-open
}

function toRegionRow(w: RegionWire): RegionRow {
  const codes = w.branches?.map((b) => b.branchCode) ?? w.branchCodes ?? [];
  return {
    regionCode: w.regionCode,
    regionName: w.regionName,
    sortOrder: w.sortOrder ?? null,
    isActive: w.isActive,
    branchCodes: codes,
  };
}

function emptyDraft(): RegionDraft {
  return {
    isNew: true,
    lockedKey: false,
    regionCode: '',
    regionName: '',
    sortOrder: 100,
    isActive: true,
    assigned: new Set(),
    original: new Set(),
  };
}

function rowToDraft(r: RegionRow, allBranches: ReadonlyArray<BranchLite>): RegionDraft {
  // Branches currently in this region (authoritative = each branch's live
  // regionCode, so a branch reassigned elsewhere shows unchecked).
  const inRegion = new Set(
    allBranches.filter((b) => b.regionCode === r.regionCode).map((b) => b.branchCode),
  );
  return {
    isNew: false,
    lockedKey: true,
    regionCode: r.regionCode,
    regionName: r.regionName,
    sortOrder: r.sortOrder ?? 100,
    isActive: r.isActive,
    assigned: new Set(inRegion),
    original: new Set(inRegion),
  };
}

function draftToBody(d: RegionDraft, forceInactive = false) {
  // One-way reassignment: only branches newly checked (not already in this
  // region) are sent. Unchecking is a no-op server-side.
  const reassign = [...d.assigned].filter((c) => !d.original.has(c));
  return {
    regionCode: d.regionCode.trim(),
    regionName: d.regionName.trim(),
    sortOrder: d.sortOrder,
    isActive: forceInactive ? false : d.isActive,
    ...(reassign.length > 0 ? { branchCodesToReassign: reassign } : {}),
  };
}

export function RegionsPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<RegionRow> | null>(null);
  const [branches, setBranches] = useState<ReadonlyArray<BranchLite>>([]);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');
  const [refreshNonce, setRefreshNonce] = useState(0);

  const [draft, setDraft] = useState<RegionDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setError(null);
      try {
        const [rResp, bResp] = await Promise.all([
          client.retail.config.regions(),
          client.retail.config.branches(),
        ]);
        if (cancelled) return;
        if (rResp.success && Array.isArray(rResp.data)) {
          setRows((rResp.data as unknown as ReadonlyArray<RegionWire>).map(toRegionRow));
        } else {
          setError(rResp.error ?? 'Failed to load regions.');
        }
        if (bResp.success && Array.isArray(bResp.data)) {
          setBranches(
            (bResp.data as unknown as ReadonlyArray<{
              branchCode: string;
              branchName: string;
              regionCode: string | null;
            }>).map((b) => ({
              branchCode: b.branchCode,
              branchName: b.branchName,
              regionCode: b.regionCode ?? null,
            })),
          );
        }
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const branchNameByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const b of branches) m.set(b.branchCode, b.branchName);
    return m;
  }, [branches]);
  const branchRegionByCode = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const b of branches) m.set(b.branchCode, b.regionCode);
    return m;
  }, [branches]);

  const filtered = useMemo(() => {
    if (!rows) return rows;
    if (!filter.trim()) return rows;
    const n = filter.toLowerCase();
    return rows.filter(
      (r) => r.regionCode.toLowerCase().includes(n) || r.regionName.toLowerCase().includes(n),
    );
  }, [rows, filter]);

  const toggleBranch = useCallback((code: string) => {
    setDraft((d) => {
      if (!d) return d;
      const next = new Set(d.assigned);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return { ...d, assigned: next };
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (!draft.regionCode.trim()) {
      setSaveError('Region code is required.');
      return;
    }
    if (draft.regionCode.trim().length > 16) {
      setSaveError('Region code must be 16 characters or fewer.');
      return;
    }
    if (!draft.regionName.trim()) {
      setSaveError('Region name is required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await client.retail.admin.reference.regions.upsert(
        ADMIN_AUTH_TOKEN_SENTINEL,
        draftToBody(draft),
      );
      if (!resp.success) {
        setSaveError(resp.error ?? 'admin upsert returned success=false');
        return;
      }
      const reassignedCount = [...draft.assigned].filter((c) => !draft.original.has(c)).length;
      setDraft(null);
      setToast({
        tone: 'info',
        text:
          (draft.isNew ? `Created region "${draft.regionCode}".` : `Updated region "${draft.regionCode}".`) +
          (reassignedCount > 0 ? ` Reassigned ${reassignedCount} branch(es).` : ''),
      });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, client, refresh]);

  const handleDeactivate = useCallback(async () => {
    if (!draft || draft.isNew) return;
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await client.retail.admin.reference.regions.upsert(
        ADMIN_AUTH_TOKEN_SENTINEL,
        draftToBody(draft, true),
      );
      if (!resp.success) {
        setSaveError(resp.error ?? 'deactivate returned success=false');
        return;
      }
      setDraft(null);
      setToast({
        tone: 'warn',
        text: `Deactivated region "${draft.regionCode}". History preserved; reassign its branches before reuse.`,
      });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, client, refresh]);

  const columns: ReadonlyArray<DataTableColumn<RegionRow>> = useMemo(
    () => [
      { header: 'Code', cell: (r) => <code className="cell-mono--bold">{r.regionCode}</code> },
      { header: 'Name', cell: (r) => r.regionName },
      {
        header: 'Sort',
        cell: (r) =>
          r.sortOrder == null ? <span className="cell-dim">—</span> : <span className="cell-mono">{r.sortOrder}</span>,
      },
      {
        header: 'Branches',
        cell: (r) => (
          <div className="pill-stack">
            {r.branchCodes.length === 0 ? (
              <span className="cell-dim">none</span>
            ) : (
              r.branchCodes.map((code) => (
                <Pill key={code} tone="muted">
                  {code}
                </Pill>
              ))
            )}
          </div>
        ),
      },
      {
        header: 'Status',
        cell: (r) => (r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>),
      },
      {
        header: '',
        cell: (r) => <Button onClick={() => { setSaveError(null); setDraft(rowToDraft(r, branches)); }}>Edit</Button>,
      },
    ],
    [branches],
  );

  if (error) return <Banner tone="err">Could not load regions: {error}</Banner>;
  if (!rows) return <Spinner label="Loading regions…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card
        title={`Regions — ${filtered?.length ?? 0} of ${rows.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <TextField
                label=""
                value={filter}
                onChange={setFilter}
                placeholder="Filter by code or name…"
              />
            </div>
            <Button
              variant="primary"
              onClick={() => {
                setSaveError(null);
                setDraft(emptyDraft());
              }}
            >
              + Add Region
            </Button>
          </div>
        }
      >
        <DataTable<RegionRow>
          columns={columns}
          rows={filtered ?? []}
          rowKey={(r) => r.regionCode}
          emptyState={<p className="empty">No regions match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'Add Region' : `Edit region ${draft?.regionCode ?? ''}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={680}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              {!draft.isNew && draft.isActive && (
                <Button onClick={() => void handleDeactivate()} disabled={saving}>
                  Deactivate
                </Button>
              )}
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <TextField
              label="Region code"
              value={draft.regionCode}
              onChange={(v) => setDraft({ ...draft, regionCode: v.slice(0, 16) })}
              placeholder="e.g. PhelpsCounty"
              disabled={draft.lockedKey}
              monospace
              maxLength={16}
              hint={
                draft.lockedKey
                  ? 'Region code is permanent — deactivate and recreate to re-key.'
                  : 'Up to 16 characters. Stable key referenced by branches + users.'
              }
            />

            <TextField
              label="Region name"
              value={draft.regionName}
              onChange={(v) => setDraft({ ...draft, regionName: v })}
              placeholder="e.g. Phelps County"
            />

            <NumberField
              label="Sort order"
              value={draft.sortOrder}
              onChange={(v) => setDraft({ ...draft, sortOrder: v ?? 100 })}
              min={0}
              hint="Display ordering across region pickers. Lower sorts first."
            />

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="Inactive regions stay for history but aren't selectable on new work."
            />

            <div className="form-field">
              <span className="form-field__label">Branches in this region</span>
              <div className="role-grid">
                {branches.length === 0 && <span className="cell-dim">No branches loaded.</span>}
                {branches.map((b) => {
                  const checked = draft.assigned.has(b.branchCode);
                  const liveRegion = branchRegionByCode.get(b.branchCode) ?? null;
                  const elsewhere = liveRegion && liveRegion !== draft.regionCode;
                  return (
                    <label key={b.branchCode} className="role-grid__cell">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleBranch(b.branchCode)}
                      />
                      <span>
                        <code className="role-grid__code">{b.branchCode}</code>
                        <span className="role-grid__name"> — {b.branchName}</span>
                        {elsewhere && !draft.original.has(b.branchCode) && (
                          <span className="cell-dim"> (currently in {liveRegion})</span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
              <span className="form-field__hint">
                Checking a branch reassigns it into this region on save. Unchecking does not move it
                out — open the branch&apos;s new region to reassign it. (Branch → region is one-to-many.)
              </span>
            </div>
          </div>
        )}
      </ModalShell>
    </>
  );
}
